import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Plus, Edit, Trash2, Search, CheckCircle, XCircle, Clock, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const WorkflowConfig = () => {
  const [activeTab, setActiveTab] = useState('configs');
  const [configs, setConfigs] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [approvalFilter, setApprovalFilter] = useState('all');

  const emptyForm = { name: '', module: 'purchase', flow_type: 'purchase', levels: [
    { level: 1, name: 'Level 1 Approval', role: 'manager', action: 'approve' },
    { level: 2, name: 'Production Order', role: 'production_manager', action: 'create_production' },
    { level: 3, name: 'Finished Goods Release', role: 'admin', action: 'release' }
  ]};
  const [formData, setFormData] = useState({ ...emptyForm });

  useEffect(() => { fetchData(); }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [cfgRes, aprRes, usersRes] = await Promise.all([api.get('/workflow/configs'), api.get('/workflow/approvals'), api.get('/admin/users')]);
      setConfigs(cfgRes.data); setApprovals(aprRes.data); setUsers(usersRes.data);
    } catch(e) { console.error(e); } finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setFormData({ ...emptyForm }); setShowModal(true); };
  const openEdit = (c) => { setEditing(c); setFormData({ name: c.name, module: c.module, flow_type: c.flow_type, levels: c.levels || [] }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/workflow/configs/${editing.id}`, formData);
      else await api.post('/workflow/configs', formData);
      setShowModal(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteConfig = async (id, name) => {
    if (!window.confirm(`Delete workflow "${name}"?`)) return;
    try { await api.delete(`/workflow/configs/${id}`); fetchData(); } catch(e) { toast.error('Failed'); }
  };

  const approveRequest = async (id) => {
    const remarks = window.prompt('Approval remarks (optional):') || '';
    try { await api.post(`/workflow/approvals/${id}/approve`, { remarks }); fetchData(); } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const rejectRequest = async (id) => {
    const remarks = window.prompt('Rejection reason:');
    if (!remarks) return;
    try { await api.post(`/workflow/approvals/${id}/reject`, { remarks }); fetchData(); } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const updateLevel = (idx, field, val) => {
    const newLevels = [...formData.levels];
    newLevels[idx] = { ...newLevels[idx], [field]: val };
    setFormData({ ...formData, levels: newLevels });
  };

  const addLevel = () => {
    const next = formData.levels.length + 1;
    setFormData({ ...formData, levels: [...formData.levels, { level: next, name: `Level ${next}`, role: 'manager', action: 'approve' }] });
  };

  const removeLevel = (idx) => {
    const updated = formData.levels.filter((_, i) => i !== idx).map((l, i) => ({ ...l, level: i + 1 }));
    setFormData({ ...formData, levels: updated });
  };

  const filteredApprovals = approvals.filter(a => {
    const matchSearch = (a.document_number || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = approvalFilter === 'all' || a.status === approvalFilter;
    return matchSearch && matchStatus;
  });

  const getStatusColor = (s) => {
    const m = { pending: '#D97706', approved: '#059669', rejected: '#DC2626', waiting: '#9CA3AF' };
    return m[s] || '#9CA3AF';
  };

  return (
    <div data-testid="workflow-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Workflow & Approvals</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {[{ key: 'configs', label: 'Workflow Config' }, { key: 'approvals', label: `Approval Queue (${approvals.filter(a => a.status === 'pending').length})` }].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} className={`btn btn-sm ${activeTab === t.key ? 'btn-primary' : 'btn-secondary'}`} data-testid={`tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      {activeTab === 'configs' && (
        <>
          <div className="flex items-center justify-between mb-4">
            <p style={{ color: 'var(--text-tertiary)', fontSize: '13px', margin: 0 }}>Define multi-level approval workflows for Purchase, Sales, and Production modules</p>
            <button onClick={openCreate} className="btn btn-primary" data-testid="add-config-btn"><Plus size={16} /> Add Workflow</button>
          </div>
          <div className="card">
            {loading ? <div>Loading...</div> : (
              <div className="table-container">
                <table>
                  <thead><tr><th>Name</th><th>Module</th><th>Flow</th><th>Levels</th><th>Active</th><th>Actions</th></tr></thead>
                  <tbody>
                    {configs.length === 0 ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No workflows defined</td></tr> : configs.map(c => (
                      <tr key={c.id} data-testid={`config-row-${c.id}`}>
                        <td style={{ fontWeight: 500 }}>{c.name}</td>
                        <td><span className="badge badge-info">{c.module}</span></td>
                        <td>{c.flow_type}</td>
                        <td>
                          <div className="flex gap-1" style={{ flexWrap: 'wrap' }}>
                            {(c.levels || []).map((l, i) => (
                              <span key={i} className="badge" style={{ background: 'var(--bg-surface-alt)', fontSize: '10px' }}>{l.name}</span>
                            ))}
                          </div>
                        </td>
                        <td><span className={`badge ${c.is_active ? 'badge-success' : 'badge-danger'}`}>{c.is_active ? 'Active' : 'Inactive'}</span></td>
                        <td>
                          <div className="flex gap-2">
                            <button onClick={() => openEdit(c)} className="btn btn-ghost btn-sm"><Edit size={14} /></button>
                            <button onClick={() => deleteConfig(c.id, c.name)} className="btn btn-ghost btn-sm"><Trash2 size={14} color="#DC2626" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'approvals' && (
        <>
          <div className="card mb-4">
            <div className="flex gap-4" style={{ flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
                <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
                <input type="text" className="form-input" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-approvals" />
              </div>
              <select className="form-select" style={{ width: '140px' }} value={approvalFilter} onChange={(e) => setApprovalFilter(e.target.value)} data-testid="approval-status-filter">
                <option value="all">All</option><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
          <div className="card">
            {loading ? <div>Loading...</div> : (
              <div className="table-container">
                <table>
                  <thead><tr><th>Document</th><th>Type</th><th>Requested By</th><th>Current Level</th><th>Levels</th><th>Status</th><th>Date</th><th>Actions</th></tr></thead>
                  <tbody>
                    {filteredApprovals.length === 0 ? <tr><td colSpan={8} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No approval requests</td></tr> : filteredApprovals.map(a => (
                      <tr key={a.id} data-testid={`approval-row-${a.id}`}>
                        <td className="font-mono">{a.document_number || '-'}</td>
                        <td><span className="badge badge-info">{a.document_type?.replace('_', ' ')}</span></td>
                        <td>{a.requested_by_name}</td>
                        <td className="tabular-nums">L{a.current_level}/{a.max_level}</td>
                        <td>
                          <div className="flex gap-1">
                            {(a.levels_status || []).map((l, i) => (
                              <div key={i} style={{ width: '20px', height: '20px', borderRadius: '50%', background: getStatusColor(l.status), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#fff', fontWeight: 700 }} title={`L${l.level}: ${l.status}`}>{l.level}</div>
                            ))}
                          </div>
                        </td>
                        <td><span className={`badge ${a.status === 'approved' ? 'badge-success' : a.status === 'rejected' ? 'badge-danger' : 'badge-warning'}`}>{a.status}</span></td>
                        <td className="text-sm">{a.created_at ? new Date(a.created_at).toLocaleDateString() : '-'}</td>
                        <td>
                          {a.status === 'pending' && (
                            <div className="flex gap-2">
                              <button onClick={() => approveRequest(a.id)} className="btn btn-ghost btn-sm" title="Approve" data-testid={`approve-${a.id}`}><CheckCircle size={14} color="#059669" /></button>
                              <button onClick={() => rejectRequest(a.id)} className="btn btn-ghost btn-sm" title="Reject" data-testid={`reject-${a.id}`}><XCircle size={14} color="#DC2626" /></button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {showModal && (
        <div className="modal-overlay" data-testid="workflow-modal">
          <div className="modal-content" style={{ maxWidth: '650px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editing ? 'Edit Workflow' : 'Create Workflow'}</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2">
                  <div className="form-group"><label className="form-label">Name *</label><input type="text" className="form-input" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="wf-name" /></div>
                  <div className="form-group">
                    <label className="form-label">Module</label>
                    <select className="form-select" value={formData.module} onChange={(e) => setFormData({...formData, module: e.target.value})} data-testid="wf-module">
                      <option value="purchase">Purchase</option><option value="sales">Sales</option><option value="production">Production</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div className="flex items-center justify-between" style={{ marginBottom: '8px' }}>
                    <label className="form-label" style={{ fontWeight: 600, margin: 0 }}>Approval Levels</label>
                    <button type="button" onClick={addLevel} className="btn btn-secondary btn-sm" data-testid="add-level-btn"><Plus size={14} /> Level</button>
                  </div>
                  {formData.levels.map((l, i) => (
                    <div key={i} className="flex gap-2 mb-2" style={{ alignItems: 'center', background: 'var(--bg-surface-alt)', padding: '8px 12px', borderRadius: '4px' }}>
                      <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--brand-primary)', minWidth: '30px' }}>L{l.level}</span>
                      <input type="text" className="form-input" value={l.name} onChange={(e) => updateLevel(i, 'name', e.target.value)} placeholder="Level name" style={{ flex: 2 }} />
                      <select className="form-select" value={l.approver_id || ''} onChange={(e) => { const u = users.find(x => x.id === e.target.value); updateLevel(i, 'approver_id', e.target.value); updateLevel(i, 'approver_name', u?.name || ''); updateLevel(i, 'role', u?.name || l.role); }} style={{ flex: 2 }}>
                        <option value="">Select Approver...</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
                      </select>
                      <select className="form-select" value={l.action} onChange={(e) => updateLevel(i, 'action', e.target.value)} style={{ flex: 1 }}>
                        <option value="approve">Approve</option><option value="create_production">Create PO</option><option value="release">Release</option><option value="review">Review</option>
                      </select>
                      {formData.levels.length > 1 && <button type="button" onClick={() => removeLevel(i)} className="btn btn-ghost btn-sm"><Trash2 size={14} color="#DC2626" /></button>}
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-wf-btn">{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowConfig;
